<script setup>
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  deals: { type: Array, default: () => [] },
  timeline: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['openDeal']);
const { t } = useI18n();

const money = deal =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: deal.currency || 'COP',
    maximumFractionDigits: 0,
  }).format(Number(deal.amount || 0));
const dateTime = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
</script>

<template>
  <section class="border-t border-n-strong pt-6">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('COMPANIES.DETAIL.COMMERCIAL.DEALS') }}
    </h2>
    <div v-if="isLoading" class="flex justify-center py-8"><Spinner /></div>
    <div v-else-if="deals.length" class="mt-4 grid gap-3 sm:grid-cols-2">
      <button
        v-for="deal in deals"
        :key="deal.id"
        type="button"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 text-start hover:border-n-brand"
        @click="emit('openDeal', deal.id)"
      >
        <strong class="block text-sm text-n-slate-12">{{ deal.name }}</strong>
        <span class="mt-1 block text-xs text-n-slate-10">{{
          deal.currentStage.name
        }}</span>
        <span class="mt-3 block text-sm font-medium text-n-slate-12">{{
          money(deal)
        }}</span>
      </button>
    </div>
    <p
      v-else
      class="mt-4 rounded-xl border border-dashed border-n-weak p-5 text-center text-sm text-n-slate-10"
    >
      {{ t('COMPANIES.DETAIL.COMMERCIAL.NO_DEALS') }}
    </p>
  </section>

  <section class="border-t border-n-strong pt-6">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('COMPANIES.DETAIL.COMMERCIAL.INTERACTIONS') }}
    </h2>
    <div v-if="timeline.length" class="mt-4 space-y-3">
      <article
        v-for="entry in timeline.slice(0, 20)"
        :key="`${entry.type}-${entry.id}-${entry.deal.id}`"
        class="rounded-xl border border-n-weak p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <strong class="text-sm text-n-slate-12">{{ entry.deal.name }}</strong>
          <time class="text-xs text-n-slate-9">{{
            dateTime(entry.occurredAt)
          }}</time>
        </div>
        <p class="mt-1 text-xs text-n-slate-10">
          {{
            entry.notes ||
            entry.body ||
            entry.name ||
            entry.toStage?.name ||
            entry.subtype
          }}
        </p>
      </article>
    </div>
    <p v-else class="mt-4 text-sm text-n-slate-10">
      {{ t('COMPANIES.DETAIL.COMMERCIAL.NO_INTERACTIONS') }}
    </p>
  </section>
</template>
